import { Box, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CredicuotasIcon from './CredicuotasIcon';
import i18n from '../../../common/i18n';
import { AUTOGESTION, CONTRACT_WT, ORIGINACION_WT } from '../../../../api/env';
import safeGetEnv from '../../../../prerender';
import { answerLinkSx, answerTextSx } from '../style/helpStyles';

export const HELP_CATEGORIES = [
  { id: 'about', title: i18n.helpAboutCredicuotas, Icon: CredicuotasIcon },
  { id: 'wt', title: i18n.helpWt, Icon: PersonOutlineIcon },
  { id: 'wc', title: i18n.helpWc, Icon: ShoppingCartOutlinedIcon },
  { id: 'commerce', title: i18n.helpCommerce, Icon: StorefrontOutlinedIcon },
];

export const DEFAULT_HELP_CATEGORY = HELP_CATEGORIES[0].id;

const paragraph = (content, key) => (
  <Typography key={key} component="p" sx={answerTextSx}>{content}</Typography>
);

const externalLink = (href, label) => (
  <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={answerLinkSx}>
    {label}
  </Box>
);

const mailLink = (address, label) => (
  <Box component="a" href={`mailto:${address}`} rel="noopener noreferrer" sx={answerLinkSx}>
    {label}
  </Box>
);

const getHelpFaqGroups = () => {
  const originationLink = safeGetEnv(ORIGINACION_WT);
  const selfManagementLink = safeGetEnv(AUTOGESTION);
  const contractLink = safeGetEnv(CONTRACT_WT);

  const ourWeb = externalLink(originationLink, i18n.helpOurWeb);
  // Mismo destino que ourWeb, pero la frase de "¿Qué ofrecemos?" pide otra etiqueta.
  const webPage = externalLink(originationLink, i18n.helpWebPage);

  return [
    {
      id: 'about',
      title: i18n.helpAboutCredicuotas,
      items: [
        {
          id: 'about-1',
          question: i18n.helpWhoWeAre,
          answer: paragraph(i18n.helpAnswerWhoWeAre),
        },
        {
          id: 'about-2',
          question: i18n.helpWhoWeAreSecondQuestion,
          answer: paragraph(
            <>
              {i18n.helpAnswerWhoWeAreSecondAnswerOne}
              {webPage}
              {i18n.helpAnswerWhoWeAreSecondAnswerTwo}
            </>,
          ),
        },
      ],
    },
    {
      id: 'wt',
      title: i18n.helpWt,
      items: [
        {
          id: 'wt-2',
          question: i18n.helpWtSecondQuestion,
          answer: (
            <>
              {paragraph(i18n.helpWtSecondAnswerIntro, 'wt-2-a')}
              <Box component="ul" sx={{ ...answerTextSx, margin: '10px 0 0', paddingLeft: '20px' }}>
                <li>{i18n.helpWtSecondAnswerItemOne}</li>
                <li>{i18n.helpWtSecondAnswerItemTwo}</li>
                <li>{i18n.helpWtSecondAnswerItemThree}</li>
                <li>{i18n.helpWtSecondAnswerItemFour}</li>
              </Box>
            </>
          ),
        },
        {
          id: 'wt-4',
          question: i18n.helpWtFourthQuestion,
          answer: (
            <>
              {paragraph(i18n.helpWtFourthAnswerOne, 'wt-4-a')}
              {paragraph(i18n.helpWtFourthAnswerTwo, 'wt-4-b')}
            </>
          ),
        },
        {
          id: 'wt-5',
          question: i18n.helpWtFifthQuestion,
          answer: paragraph(i18n.helpWtFifthAnswerOne),
        },
        {
          id: 'wt-6',
          question: i18n.helpWtSixthQuestion,
          answer: (
            <>
              {paragraph(
                <>
                  {i18n.helpWtSixthAnswerOne}
                  {ourWeb}
                  {i18n.helpWtSixthAnswerTwo}
                </>,
                'wt-6-a',
              )}
              {paragraph(i18n.helpWtSixthAnswerThree, 'wt-6-b')}
              {paragraph(i18n.helpWtSixthAnswerFour, 'wt-6-c')}
              {paragraph(i18n.helpWtSixthAnswerFive, 'wt-6-d')}
            </>
          ),
        },
        {
          id: 'wt-7',
          question: i18n.helpWtSeventhQuestion,
          answer: paragraph(i18n.helpWtSeventhAnswer),
        },
        {
          id: 'wt-8',
          question: i18n.helpWtEighthQuestion,
          answer: (
            <>
              {paragraph(i18n.helpWtEighthAnswerOne, 'wt-8-a')}
              {paragraph(
                <>
                  {i18n.helpWtEighthAnswerTwo}
                  <strong>{i18n.helpWtEighthAnswerThree}</strong>
                </>,
                'wt-8-b',
              )}
              {paragraph(
                <>
                  {i18n.helpWtEighthAnswerFour}
                  {externalLink(selfManagementLink, i18n.helpWtEighthAnswerFive)}
                </>,
                'wt-8-c',
              )}
            </>
          ),
        },
        {
          id: 'wt-9',
          question: i18n.helpWtNinethQuestion,
          answer: (
            <>
              {paragraph(i18n.helpWtNinethAnswerOne, 'wt-9-a')}
              {paragraph(i18n.helpWtNinethAnswerTwo, 'wt-9-b')}
              {paragraph(i18n.helpWtNinethAnswerThree, 'wt-9-c')}
              {paragraph(i18n.helpWtNinethAnswerFourth, 'wt-9-d')}
            </>
          ),
        },
        {
          id: 'wt-10',
          question: i18n.helpWtTenthQuestion,
          answer: paragraph(
            <>
              {i18n.helpWtTenthAnswerOne}
              {ourWeb}
              {i18n.helpWtTenthAnswerTwo}
            </>,
          ),
        },
        {
          id: 'wt-11',
          question: i18n.helpWtEleventhQuestion,
          answer: (
            <>
              {paragraph(i18n.helpWtEleventhAnswerOne, 'wt-11-a')}
              {paragraph(externalLink(contractLink, i18n.helpWtEleventhAnswerTwo), 'wt-11-b')}
            </>
          ),
        },
      ],
    },
    {
      id: 'wc',
      title: i18n.helpWc,
      items: [
        {
          id: 'wc-1',
          question: i18n.helpWcFirstQuestion,
          answer: paragraph(i18n.helpWcFirstAnswer),
        },
        {
          id: 'wc-2',
          question: i18n.helpWcSecondQuestion,
          answer: paragraph(i18n.helpWcSecondAnswer),
        },
        {
          id: 'wc-3',
          question: i18n.helpWcThirdQuestion,
          answer: paragraph(i18n.helpWcThirdAnswer),
        },
        {
          id: 'wc-4',
          question: i18n.helpWcFourthQuestion,
          answer: paragraph(i18n.helpWcFourthAnswer),
        },
        {
          id: 'wc-5',
          question: i18n.helpWcFifthQuestion,
          answer: paragraph(i18n.helpWcFifthAnswer),
        },
        {
          id: 'wc-6',
          question: i18n.helpWcSixthQuestion,
          answer: (
            <>
              {paragraph(i18n.helpWcSixthAnswerOne, 'wc-6-a')}
              {paragraph(i18n.helpWcSixthAnswerTwo, 'wc-6-b')}
              <Box component="ul" sx={{ ...answerTextSx, margin: '10px 0 0', paddingLeft: '20px' }}>
                <li>
                  {i18n.helpWcSixthAnswerThree}
                  {externalLink(selfManagementLink, i18n.helpWcSixthAnswerFour)}
                  {i18n.helpWcSixthAnswerFive}
                </li>
                <li>{i18n.helpWcSixthAnswerSix}</li>
              </Box>
            </>
          ),
        },
        {
          id: 'wc-7',
          question: i18n.helpWcSeventhQuestion,
          answer: paragraph(i18n.helpWcSeventhAnswer),
        },
        {
          id: 'wc-8',
          question: i18n.helpWcEighthQuestion,
          answer: paragraph(
            <>
              {i18n.helpWcEighthAnswerOne}
              {externalLink(originationLink, i18n.helpWcEighthAnswerTwo)}
            </>,
          ),
        },
      ],
    },
    {
      id: 'commerce',
      title: i18n.helpCommerce,
      items: [
        {
          id: 'commerce-1',
          question: i18n.helpCommerceFirstQuestion,
          answer: paragraph(
            <>
              {i18n.helpCommerceFirstAnswerOne}
              {mailLink(i18n.helpCommerceFirstAnswerTwo, i18n.helpCommerceFirstAnswerTwo)}
              {i18n.helpCommerceFirstAnswerThree}
            </>,
          ),
        },
        {
          id: 'commerce-2',
          question: i18n.helpCommerceSecondQuestion,
          answer: paragraph(i18n.helpCommerceSecondAnswer),
        },
        {
          id: 'commerce-3',
          question: i18n.helpCommerceThirdQuestion,
          answer: paragraph(i18n.helpCommerceThirdAnswer),
        },
        {
          id: 'commerce-4',
          question: i18n.helpCommerceFourthQuestion,
          answer: paragraph(i18n.helpCommerceFourthAnswer),
        },
        {
          id: 'commerce-5',
          question: i18n.helpCommerceFifthQuestion,
          answer: paragraph(i18n.helpCommerceFifthAnswer),
        },
      ],
    },
  ];
};

export default getHelpFaqGroups;
